import React, { useState, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { Search, UploadCloud, Folder, Loader2, Music, Play, Pause, Download, X, Trash2, Rewind, FastForward } from 'lucide-react';
import services from '../../API';
import { toast } from 'react-toastify';
import ConfirmModal from '../Modals/ConfirmModal';
import { CircularLoader } from '../Loader/CircularLoader';
import { Fragment } from 'react';
import '@headlessui/react';

const AssetItem = ({ asset, onPreview, onDownload, isSelected, onToggleSelection, togglePlay, playingTrackId, loadingTrackId, onDelete, onSkip }) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const isImage = asset.type === 'image' || asset.mimeType?.startsWith('image/');
    const isVideo = asset.type === 'video' || asset.mimeType?.startsWith('video/');

    useEffect(() => {
        if (!isImage && !isVideo) {
            setIsLoaded(true);
        }
    }, [isImage, isVideo]);

    return (
        <div
            className="group relative flex min-w-0 flex-col cursor-pointer"
            onClick={() => onPreview(asset)}
        >
            <div className="relative aspect-[3/4] rounded-2xl overflow-hidden bg-white dark:bg-[#111111] ring-1 ring-black/5 dark:ring-white/10 shadow-sm group-hover:shadow-2xl group-hover:ring-orange-500/50 group-hover:scale-[1.02] transition-all duration-300">

                {/* Loading State */}
                {!isLoaded && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-50 dark:bg-white/5 z-10">
                        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
                    </div>
                )}

                {isImage ? (
                    <img
                        src={asset.url}
                        alt={asset.name}
                        className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-110 ${!isLoaded ? 'opacity-0' : 'opacity-100'}`}
                        onLoad={() => setIsLoaded(true)}
                        onError={() => setIsLoaded(true)}
                    />
                ) : isVideo ? (
                    <div className="relative w-full h-full">
                        <video
                            src={asset.url}
                            className={`w-full h-full object-cover ${!isLoaded ? 'opacity-0' : 'opacity-100'}`}
                            onLoadedData={() => setIsLoaded(true)}
                            onError={() => setIsLoaded(true)}
                        />
                        <div className={`absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/40 transition-colors ${!isLoaded ? 'hidden' : ''}`}>
                            <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/40 shadow-lg group-hover:scale-110 transition-transform">
                                <Play className="w-5 h-5 text-white fill-white ml-0.5" />
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center p-6 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-white/5 dark:to-white/10">
                        <div className="w-16 h-16 rounded-2xl bg-white dark:bg-black shadow-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                            <Music className="w-8 h-8 text-orange-500" />
                        </div>
                    </div>
                )}

                {/* Delete Icon */}
                {onDelete && !isImage && !isVideo && (
                    <div className="absolute top-3 right-3 z-20 pointer-events-auto opacity-0 group-hover:opacity-100 transition-opacity">
                        <button 
                            onClick={(e) => { e.stopPropagation(); onDelete(asset); }}
                            className="w-8 h-8 rounded-full bg-red-500/80 hover:bg-red-600 text-white flex items-center justify-center backdrop-blur-sm shadow-md transition-colors"
                        >
                            <Trash2 className="w-4 h-4" />
                        </button>
                    </div>
                )}

                {/* Selection Checkbox */}
                <div
                    className={`absolute top-3 left-3 z-20 selection-checkbox ${isSelected || "group-hover:opacity-100 opacity-0"
                        } transition-opacity duration-200`}
                    onClick={(e) => {
                        e.stopPropagation();
                        onToggleSelection(asset._id);
                    }}
                >
                    <div
                        className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all ${isSelected
                            ? "bg-blue-600 border-blue-600 shadow-lg shadow-blue-500/30"
                            : "bg-white/80 dark:bg-black/40 border-white/40 hover:border-white backdrop-blur-md"
                            }`}
                    >
                        {isSelected && (
                            <svg
                                className="w-4 h-4 text-white"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                            >
                                <path
                                    fillRule="evenodd"
                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        )}
                    </div>
                </div>

                {/* Overlays - Show only when loaded */}
                {isLoaded && (
                    <>
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300" />

                        {/* Hover Actions */}
                        <div className={`absolute bottom-0 left-0 right-0 p-4 transition-all duration-300 flex items-center justify-between ${playingTrackId === asset._id ? 'translate-y-0 opacity-100' : 'translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100'}`}>
                            <button
                                className="p-2 rounded-lg bg-white/20 backdrop-blur-md border border-white/30 text-white hover:bg-white hover:text-orange-600 transition-colors"
                                onClick={(e) => onDownload(asset, e)}
                                title="Download"
                            >
                                <Download className="w-4 h-4" />
                            </button>
                            {(!isImage && !isVideo) ? (
                                <div className="flex items-center gap-1.5">
                                    {playingTrackId === asset._id && (
                                        <button 
                                            onClick={(e) => onSkip(e, -5)} 
                                            className="p-1.5 rounded-lg bg-black/40 hover:bg-black/60 text-white backdrop-blur-md transition-colors"
                                            title="Skip backward 5s"
                                        >
                                            <Rewind className="w-3 h-3 fill-current" />
                                        </button>
                                    )}
                                    <button
                                        onClick={(e) => togglePlay(e, asset)}
                                        className={`px-3 py-1.5 rounded-lg text-white text-xs font-bold uppercase tracking-wider shadow-lg transition-colors flex items-center gap-1 ${playingTrackId === asset._id ? 'bg-orange-600 hover:bg-orange-700' : 'bg-orange-500 hover:bg-orange-600'}`}
                                    >
                                        {loadingTrackId === asset._id ? (
                                            <><Loader2 className="w-3 h-3 animate-spin" /> Loading</>
                                        ) : playingTrackId === asset._id ? (
                                            <><Pause className="w-3 h-3 fill-current" /> Pause</>
                                        ) : (
                                            <><Play className="w-3 h-3 fill-current" /> Play</>
                                        )}
                                    </button>
                                    {playingTrackId === asset._id && (
                                        <button 
                                            onClick={(e) => onSkip(e, 5)} 
                                            className="p-1.5 rounded-lg bg-black/40 hover:bg-black/60 text-white backdrop-blur-md transition-colors"
                                            title="Skip forward 5s"
                                        >
                                            <FastForward className="w-3 h-3 fill-current" />
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <button 
                                    onClick={(e) => { e.stopPropagation(); onPreview(asset); }}
                                    className="px-3 py-1.5 rounded-lg bg-orange-500 text-white text-xs font-bold uppercase tracking-wider shadow-lg hover:bg-orange-600 transition-colors"
                                >
                                    Preview
                                </button>
                            )}
                        </div>
                    </>
                )}
            </div>

            <div className="mt-4 px-1">
                <div className="text-[10px] uppercase font-bold text-orange-500 tracking-wider mb-1.5 flex items-center gap-1.5 opacity-80">
                    {asset.type || 'ASSET'}
                </div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate group-hover:text-orange-500 transition-colors cursor-pointer" title={asset.name}>
                    {asset.name}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5 lg:mt-1 truncate opacity-60">
                    {asset.mimeType || 'Unknown Type'}
                </p>
            </div>
        </div>
    );
};

const AssetsLibraryPage = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const [allAssets, setAllAssets] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState('creativeAssets');

    // Preview State
    const [previewAsset, setPreviewAsset] = useState(null);
    const [, setIsUploading] = useState(false);
    const [selectedAssets, setSelectedAssets] = useState([]);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [singleDeleteModalOpen, setSingleDeleteModalOpen] = useState(false);
    const [assetToDelete, setAssetToDelete] = useState(null);
    const [isSingleDeleting, setIsSingleDeleting] = useState(false);

    const [playingTrackId, setPlayingTrackId] = useState(null);
    const [loadingTrackId, setLoadingTrackId] = useState(null);
    const audioRef = useRef(null);

    useEffect(() => {
        audioRef.current = new Audio();
        audioRef.current.onended = () => {
            setPlayingTrackId(null);
            setLoadingTrackId(null);
        };
        audioRef.current.onplaying = () => setLoadingTrackId(null);
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.src = '';
            }
        };
    }, []);

    const handleSkip = (e, seconds) => {
        e.stopPropagation();
        if (audioRef.current) {
            audioRef.current.currentTime = Math.max(0, Math.min(audioRef.current.currentTime + seconds, audioRef.current.duration || 0));
        }
    };

    useEffect(() => {
        if (audioRef.current) {
            audioRef.current.pause();
        }
        setPlayingTrackId(null);
        setLoadingTrackId(null);
    }, [selectedCategory]);

    const togglePlay = (e, track) => {
        e.stopPropagation();
        if (playingTrackId === track._id || loadingTrackId === track._id) {
            audioRef.current.pause();
            setPlayingTrackId(null);
            setLoadingTrackId(null);
        } else {
            if (audioRef.current) {
                setLoadingTrackId(track._id);
                setPlayingTrackId(track._id);
                audioRef.current.src = track.url;
                audioRef.current.play().catch(err => {
                    console.error("Playback failed:", err);
                    setPlayingTrackId(null);
                    setLoadingTrackId(null);
                });
            }
        }
    };

    const fetchAssets = async () => {
        setIsLoading(true);
        try {
            let combinedAssets = [];

            // 1. Standard assets
            try {
                const response = await services.getAssets();
                if (response?.data?.assets) {
                    combinedAssets = response.data.assets.filter(
                        asset => asset.folderType !== 'noteAssets' && asset.type !== 'other'
                    );
                }
            } catch (err) {
                console.error("Error fetching standard assets:", err);
            }

            // 2. Generated Music
            try {
                const musicRes = await services.getMusicAssets({ limit: 50, skip: 0, type: 'music' });
                if (musicRes?.data?.musics) {
                    const generatedMusic = musicRes.data.musics.map(m => ({
                        _id: m._id || m.id,
                        name: m.name || 'Generated Music',
                        url: m.musicUrl || m.url,
                        mimeType: 'audio/mpeg',
                        type: 'audio',
                        folderType: 'music',
                        duration: m.durationMs ? Math.round(m.durationMs / 1000) : 30
                    }));
                    combinedAssets = [...combinedAssets, ...generatedMusic];
                }
            } catch (err) {
                console.error("Error fetching music assets:", err);
            }

            // 3. Generated Sound Effects
            try {
                const sfxRes = await services.getMusicAssets({ limit: 50, skip: 0, type: 'soundEffect' });
                if (sfxRes?.data?.musics) {
                    const generatedSfx = sfxRes.data.musics.map(m => ({
                        _id: m._id || m.id,
                        name: m.name || 'Generated SFX',
                        url: m.musicUrl || m.url,
                        mimeType: 'audio/mpeg',
                        type: 'audio',
                        folderType: 'soundEffect',
                        duration: m.durationMs ? Math.round(m.durationMs / 1000) : 5
                    }));
                    combinedAssets = [...combinedAssets, ...generatedSfx];
                }
            } catch (err) {
                console.error("Error fetching sfx assets:", err);
            }

            setAllAssets(combinedAssets);
        } catch (error) {
            console.error("Error fetching assets:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const uploadFiles = async (files) => {
        if (files.length === 0) return;

        setIsUploading(true);
        try {
            // Upload images sequentially
            for (const file of files) {
                const formData = new FormData();
                formData.append("file", file);

                // Using the same API as in CreateTab.js
                await services.uploadImage(formData);
            }

            // Refresh assets list
            toast.success("Assets uploaded successfully!");
            await fetchAssets();
        } catch (error) {
            console.error("Error uploading assets:", error);
            toast.error("Failed to upload assets.");
        } finally {
            setIsUploading(false);
        }
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop: uploadFiles,
        accept: {
            'video/*': ['.mp4', '.webm', '.mov', '.avi'],
            'audio/*': ['.mp3', '.wav', '.ogg', '.m4a'],
            'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'],
        },
        multiple: true,
        noKeyboard: true,
    });

    useEffect(() => {
        fetchAssets();
    }, []);

    const toggleSelection = (id) => {
        setSelectedAssets(prev =>
            prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]
        );
    };

    const clearSelection = () => {
        setSelectedAssets([]);
    };

    const isMusicLibraryAsset = (asset) =>
        asset?.folderType === 'music' || asset?.folderType === 'soundEffect';

    const handleDeleteSelected = async () => {
        if (selectedAssets.length === 0) return;
        setIsDeleting(true);
        try {
            const standardIds = [];
            const musicIds = [];

            selectedAssets.forEach(id => {
                const asset = allAssets.find(a => a._id === id);
                if (isMusicLibraryAsset(asset)) {
                    musicIds.push(id);
                } else {
                    standardIds.push(id);
                }
            });

            if (standardIds.length > 0) await services.deleteMultipleAssets(standardIds);
            if (musicIds.length > 0) await services.deleteMusicAssets(musicIds);

            await fetchAssets();
            clearSelection();
            setDeleteModalOpen(false);
        } catch (error) {
            console.error("Error deleting assets:", error);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleDeleteClick = (asset) => {
        setAssetToDelete(asset);
        setSingleDeleteModalOpen(true);
    };

    const handleSingleDelete = async () => {
        if (!assetToDelete) return;
        setIsSingleDeleting(true);
        try {
            if (isMusicLibraryAsset(assetToDelete)) {
                await services.deleteMusicAssets([assetToDelete._id]);
            } else {
                await services.deleteMultipleAssets([assetToDelete._id]);
            }
            await fetchAssets();
            setSingleDeleteModalOpen(false);
            setAssetToDelete(null);
        } catch (error) {
            console.error("Error deleting asset:", error);
        } finally {
            setIsSingleDeleting(false);
        }
    };

    const isStandardAudio = (asset) => 
        asset.type === 'audio' ||
        asset.mimeType?.startsWith('audio/') ||
        ['mp3', 'wav', 'ogg', 'm4a'].some(ext => asset.name?.toLowerCase().endsWith(ext));

    const categories = [
        { id: 'creativeAssets', name: 'Creative Assets' },
        { id: 'music', name: 'Music' },
        { id: 'soundEffect', name: 'Sound Effects' },
    ];
    const selectedCategoryName = categories.find(category => category.id === selectedCategory)?.name || 'Creative Assets';

    // Filter assets within the selected Media Library category and search query.
    const filteredAssets = allAssets.filter(asset => {
        let categoryMatch = true;
        const isVisual = !asset.folderType && !isStandardAudio(asset);

        if (selectedCategory === 'music') {
            categoryMatch = asset.folderType === 'music' || (!asset.folderType && isStandardAudio(asset));
        } else if (selectedCategory === 'soundEffect') {
            categoryMatch = asset.folderType === 'soundEffect';
        } else {
            categoryMatch = isVisual;
        }

        const matchesSearch = asset.name?.toLowerCase().includes(searchQuery.toLowerCase());

        return categoryMatch && matchesSearch;
    });

    const handleDownload = async (asset, e) => {
        e.stopPropagation();
        try {
            const response = await fetch(asset.url);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = asset.name || `download.${asset.mimeType?.split('/')[1] || 'unknown'}`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            console.error('Download failed:', error);
            // Fallback for cross-origin if fetch fails (might need just window.open)
            window.open(asset.url, '_blank');
        }
    };

    return (
        <div className="responsive-page responsive-page--assets flex-1 h-screen overflow-hidden bg-[#FAFAFA] dark:bg-[#0A0A0A] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-8 py-6 border-b border-gray-100 dark:border-white/5">
                <div className="flex items-center gap-4">
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">Media Library</h1>
                    <span className="px-3 py-1 bg-orange-100 dark:bg-orange-500/10 text-orange-600 dark:text-orange-400 text-xs font-bold uppercase rounded-full tracking-wider">
                        {allAssets.length} Items
                    </span>
                </div>
            </div>

            {/* Search and Filters */}
            <div className="px-8 py-6 flex items-center justify-between gap-6">
                <div className="relative flex-1 max-w-2xl">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                        type="text"
                        placeholder={`Search ${selectedCategoryName.toLowerCase()}...`}
                        className="w-full bg-white dark:bg-[#111111] border border-gray-200 dark:border-white/5 rounded-2xl pl-11 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all text-gray-900 dark:text-white shadow-sm"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>
            </div>

            <div className="px-8 pb-6">
                <div
                    {...getRootProps()}
                    className={`cursor-pointer rounded-2xl border-2 border-dashed px-6 py-5 text-center transition-colors ${isDragActive
                        ? 'border-orange-500 bg-orange-50 dark:bg-orange-500/10'
                        : 'border-gray-200 bg-white hover:border-orange-300 dark:border-white/10 dark:bg-[#111111] dark:hover:border-orange-500/40'
                        }`}
                >
                    <input {...getInputProps()} />
                    <UploadCloud className="mx-auto mb-2 h-7 w-7 text-orange-500" />
                    <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                        {isDragActive ? 'Drop files to upload' : 'Drag and drop files here to upload'}
                    </p>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Images, videos, and audio files are supported.
                    </p>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-8 pb-8">
                {/* Selection Header */}
                {selectedAssets.length > 0 && (
                    <div className="sticky top-0 z-50 flex items-center justify-between bg-blue-50/90 dark:bg-blue-900/40 backdrop-blur-md border border-blue-200 dark:border-blue-800 rounded-2xl p-4 mb-8 transition-all animate-in fade-in slide-in-from-top-4 shadow-lg shadow-blue-500/10">
                        <div className="flex items-center gap-3">
                            <span className="text-blue-800 dark:text-blue-200 font-bold px-3 py-1 bg-blue-100 dark:bg-blue-500/20 rounded-full text-xs uppercase tracking-wider">
                                {selectedAssets.length} Item{selectedAssets.length !== 1 ? 's' : ''} Selected
                            </span>
                            <button
                                onClick={clearSelection}
                                className="text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800 hover:bg-white dark:hover:bg-white/10 px-4 py-2 rounded-xl text-sm font-bold transition-all shadow-sm flex items-center gap-2"
                            >
                                <X className="w-4 h-4" />
                                Clear selection
                            </button>
                        </div>
                        <button
                            onClick={() => setDeleteModalOpen(true)}
                            className="bg-red-600 hover:bg-red-700 text-white px-6 py-2.5 rounded-xl text-sm font-bold flex items-center gap-2 shadow-lg shadow-red-500/20 transition-all active:scale-95"
                        >
                            <Trash2 className="w-4 h-4" />
                            Delete selected
                        </button>
                    </div>
                )}
                <div className="mb-10">
                    <div className="inline-flex max-w-full overflow-x-auto rounded-xl border border-gray-200 bg-white p-1 shadow-sm dark:border-white/5 dark:bg-[#111111]">
                        {categories.map(category => {
                            const isActive = selectedCategory === category.id;

                            return (
                                <button
                                    key={category.id}
                                    type="button"
                                    onClick={() => setSelectedCategory(category.id)}
                                    className={`whitespace-nowrap rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${isActive
                                        ? 'bg-orange-500 text-white shadow-sm'
                                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-white'
                                        }`}
                                    aria-pressed={isActive}
                                >
                                    {category.name}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Assets Grid */}
                <div>
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-4">
                            <h2 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider">{selectedCategoryName}</h2>
                        </div>
                    </div>

                    <div className="asset-library-grid grid grid-cols-2 gap-x-3 gap-y-6 sm:gap-6 lg:grid-cols-4 lg:gap-8 xl:grid-cols-5 2xl:grid-cols-6">
                        {isLoading ? (
                            <div className="col-span-full flex flex-col items-center justify-center py-32 text-gray-400">
                                <Loader2 className="w-10 h-10 animate-spin mb-4 text-orange-500" />
                                <p className="font-medium animate-pulse">Loading assets...</p>
                            </div>
                        ) : filteredAssets.length === 0 ? (
                            <div className="col-span-full flex flex-col items-center justify-center py-32 text-gray-500 bg-white dark:bg-white/5 rounded-3xl border border-dashed border-gray-200 dark:border-white/10">
                                <div className="w-16 h-16 rounded-full bg-gray-50 dark:bg-white/5 flex items-center justify-center mb-4">
                                    <Folder className="w-8 h-8 text-gray-300 dark:text-gray-600" />
                                </div>
                                <p className="text-lg font-medium mb-1">No {selectedCategory === 'music' ? 'music' : selectedCategory === 'soundEffect' ? 'sound effect' : 'creative'} assets found</p>
                                <p className="text-sm opacity-60">Upload new assets to get started</p>
                            </div>
                        ) : (
                            filteredAssets.map(asset => {



                                return (
                                    <AssetItem
                                        key={asset._id}
                                        asset={asset}
                                        onPreview={setPreviewAsset}
                                        onDownload={handleDownload}
                                        isSelected={selectedAssets.includes(asset._id)}
                                        onToggleSelection={toggleSelection}
                                        togglePlay={togglePlay}
                                        playingTrackId={playingTrackId}
                                        loadingTrackId={loadingTrackId}
                                        onDelete={handleDeleteClick}
                                        onSkip={handleSkip}
                                    />
                                );
                            })
                        )}
                    </div>
                </div>
            </div>

            {/* Asset Preview Modal */}
            {previewAsset && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 backdrop-blur-xl p-4 md:p-8 animate-in fade-in duration-300">
                    <button
                        className="absolute top-6 right-6 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
                        onClick={() => {
                            const audioEl = document.getElementById('preview-audio');
                            if (audioEl) audioEl.pause();
                            const videoEl = document.getElementById('preview-video');
                            if (videoEl) videoEl.pause();

                            if (audioRef.current) {
                                audioRef.current.pause();
                            }
                            setPlayingTrackId(null);
                            setLoadingTrackId(null);

                            setPreviewAsset(null);
                        }}
                    >
                        <X className="w-6 h-6" />
                    </button>

                    <div className="w-full max-w-5xl flex flex-col items-center gap-8">
                        <div className="relative w-full max-h-[70vh] rounded-3xl overflow-hidden shadow-2xl ring-1 ring-white/10 bg-black/50 flex items-center justify-center">
                            {previewAsset.type === 'video' || previewAsset.mimeType?.startsWith('video/') ? (
                                <video
                                    id="preview-video"
                                    src={previewAsset.url}
                                    className="max-h-[70vh] w-auto max-w-full"
                                    controls
                                    autoPlay
                                />
                            ) : previewAsset.type === 'image' || previewAsset.mimeType?.startsWith('image/') ? (
                                <img
                                    src={previewAsset.url}
                                    alt={previewAsset.name}
                                    className="max-h-[70vh] w-auto max-w-full object-contain"
                                />
                            ) : (
                                <div className="p-12 flex flex-col items-center">
                                    <div className="w-24 h-24 rounded-3xl bg-orange-500/20 flex items-center justify-center mb-6 animate-pulse">
                                        <Music className="w-12 h-12 text-orange-500" />
                                    </div>
                                    <audio id="preview-audio" src={previewAsset.url} controls autoPlay className="w-[300px]" />
                                </div>
                            )}
                        </div>

                        <div className="flex flex-col items-center gap-4 text-center">
                            <div>
                                <h2 className="text-2xl font-bold text-white mb-2">{previewAsset.name}</h2>
                                <p className="text-white/50 text-sm font-medium uppercase tracking-widest">
                                    {previewAsset.mimeType}
                                </p>
                            </div>

                            <button
                                onClick={(e) => handleDownload(previewAsset, e)}
                                className="px-8 py-3 bg-white text-black hover:bg-gray-200 rounded-full font-bold flex items-center gap-2 transition-all active:scale-95"
                            >
                                <Download className="w-5 h-5" />
                                Download Asset
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ConfirmModal
                isOpen={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                title="Delete Assets"
                message={`Are you sure you want to delete ${selectedAssets.length === 1 ? 'this asset' : `these ${selectedAssets.length} assets`}? This action cannot be undone.`}
                confirmLabel={isDeleting ? <div className="flex justify-center"><CircularLoader size={20} /></div> : "Delete"}
                onConfirm={handleDeleteSelected}
                onCancel={() => setDeleteModalOpen(false)}
                zIndex={10000}
            />

            <ConfirmModal 
                isOpen={singleDeleteModalOpen} 
                onClose={() => { setSingleDeleteModalOpen(false); setAssetToDelete(null); }} 
                title="Delete Asset" 
                message={`Are you sure you want to delete "${assetToDelete?.name || 'this asset'}"? This action cannot be undone.`} 
                confirmLabel="Delete" 
                cancelLabel="Cancel" 
                onConfirm={handleSingleDelete} 
                zIndex={10000}
                isLoading={isSingleDeleting}
            />
        </div>
    );
};

export default AssetsLibraryPage;
